import {
  type ColumnDef,
  type ColumnFiltersState,
  createColumnHelper,
  filterFn_equalsString,
  type Row,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { CircleSlash, ExternalLink } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { DataTable, type DataTableFeatures } from "~/components/data-table";
import { Badge, Button, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import { useDataImportProjectList } from "~/features/dataImportProjects/hooks/useDataImportProjectList";
import type { DataImportProjectListRow, DipColumnFilterValues } from "~/features/dataImportProjects/types";
import { toDataImportProjectListRows } from "~/features/dataImportProjects/utils/transformers";
import { v2Route } from "~/v2/routes";

interface DataImportProjectsTableProps {
  search: string;
  excludeDeactivated: boolean;
  columnFilterValues: DipColumnFilterValues;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
}

const helper = createColumnHelper<DataTableFeatures, DataImportProjectListRow>();

// The "to" boundary of a date-range filter is a bare `yyyy-MM-dd` day. `startAt`/`endAt`
// row values are full ISO timestamps, so appending end-of-day keeps the comparison
// inclusive of that day.
function endOfDayISO(dateOnly: string): string {
  return `${dateOnly}T23:59:59.999Z`;
}

// A project that hasn't reached the relevant status yet has a null startAt/endAt (see
// `logDate` in transformers.ts) and should never match an active date-range filter. The
// built-in `betweenInclusive` filterFn treats a null row value as 0 numerically, which makes
// it match a "to"-only range but not a "from"-only range — an inconsistent result we replace
// with an explicit, testable comparison instead. ISO strings compare correctly as plain strings.
export function isInDateRangeInclusive(
  value: string | null,
  [from, to]: [string | undefined, string | undefined],
): boolean {
  if (value === null) return false;
  if (from && value < from) return false;
  if (to && value > to) return false;
  return true;
}

function filterFn_dateRangeInclusive(
  row: Row<DataTableFeatures, DataImportProjectListRow>,
  columnId: string,
  filterValue: [string | undefined, string | undefined],
): boolean {
  return isInDateRangeInclusive(row.getValue<string | null>(columnId), filterValue);
}

export function toColumnFilters(values: DipColumnFilterValues): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  if (values.seq) filters.push({ id: "seq", value: values.seq });
  if (values.name) filters.push({ id: "name", value: values.name });
  if (values.status) filters.push({ id: "statusId", value: values.status });
  if (values.references) filters.push({ id: "references", value: values.references });
  if (values.startAtFrom || values.startAtTo) {
    filters.push({
      id: "startAt",
      value: [values.startAtFrom || undefined, values.startAtTo ? endOfDayISO(values.startAtTo) : undefined],
    });
  }
  if (values.endAtFrom || values.endAtTo) {
    filters.push({
      id: "endAt",
      value: [values.endAtFrom || undefined, values.endAtTo ? endOfDayISO(values.endAtTo) : undefined],
    });
  }
  return filters;
}

export function DataImportProjectsTable({
  search,
  excludeDeactivated,
  columnFilterValues,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
}: DataImportProjectsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, refetch } = useDataImportProjectList();

  const rows = useMemo(() => {
    const allRows = toDataImportProjectListRows(data.dataImportProjects);
    return excludeDeactivated ? allRows.filter((row) => !row.isDeactivated) : allRows;
  }, [data.dataImportProjects, excludeDeactivated]);

  const columnFilters = useMemo(() => toColumnFilters(columnFilterValues), [columnFilterValues]);

  const columns: ColumnDef<DataTableFeatures, DataImportProjectListRow, any>[] = [
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        if (!info.row.original.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleSlash className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("seqLabel", {
      id: "seq",
      header: () => t("model.dataImportProject.seqID"),
      cell: (info) => <span className="font-mono text-12-regular text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("name", {
      header: () => t("model.common.name"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("statusId", {
      id: "statusId",
      header: () => t("model.dataImportProject.status"),
      filterFn: filterFn_equalsString,
      cell: (info) => <Badge tone={statusTone(info.getValue())}>{info.getValue()}</Badge>,
    }),
    helper.accessor("startAtISO", {
      id: "startAt",
      header: () => t("model.dataImportProject.startDate"),
      enableGlobalFilter: false,
      filterFn: filterFn_dateRangeInclusive,
      cell: (info) => <DateCell value={info.getValue()} />,
    }),
    helper.accessor("endAtISO", {
      id: "endAt",
      header: () => t("model.dataImportProject.endDate"),
      enableGlobalFilter: false,
      filterFn: filterFn_dateRangeInclusive,
      cell: (info) => <DateCell value={info.getValue()} />,
    }),
    helper.accessor("referencesText", {
      id: "references",
      header: () => t("model.reference.modelNames"),
      cell: (info) => <ReferenceCell references={info.row.original.references} fallback={info.getValue()} />,
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            {row.documentUrl && (
              <Button type="button" variant="outline" size="icon" asChild>
                <a
                  href={row.documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("ui.document")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink />
                </a>
              </Button>
            )}
          </div>
        );
      },
    }),
  ];

  return (
    <>
      <form
        id="dip-project-list-refresh"
        onSubmit={(event) => {
          event.preventDefault();
          void refetch();
        }}
      />
      <DataTable<DataImportProjectListRow>
        columns={columns}
        data={rows}
        emptyMessage={t("feature.common.noData")}
        globalFilter={search}
        onGlobalFilterChange={() => undefined}
        columnFilters={columnFilters}
        onColumnFiltersChange={() => undefined}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        onRowClick={(row) => navigate(v2Route.dataImportProjects.detail.$buildPath({ params: { id: row.id } }))}
      />
    </>
  );
}

function DateCell({ value }: { value: string | null }) {
  const { t } = useTranslation();
  return (
    <span className="whitespace-nowrap tabular-nums text-fg-2">
      {value ? t("date.short", { date: parseISO(value) }) : "—"}
    </span>
  );
}

function ReferenceCell({ references, fallback }: { references: string[]; fallback: string }) {
  if (references.length === 0) {
    return <span className="text-fg-3">—</span>;
  }
  return <span className="text-12-regular text-fg-2">{fallback}</span>;
}

function statusTone(statusId: string): "default" | "status-processing" | "status-approved" | "status-draft" {
  if (statusId === "Done") return "status-approved";
  if (statusId === "Planning") return "status-draft";
  if (statusId === "Importing" || statusId === "Composing") return "status-processing";
  return "default";
}
